import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Select,
  Pagination,
  Spin,
  Button,
  Typography,
  Alert,
  Affix,
  App,
} from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchBrands } from '../store/slices/brandSlice';
import { useNavigate } from 'react-router-dom';
import BrandCard from '../components/BrandCard';
import SearchSuggestions from '../components/SearchSuggestions';
import AdvancedFilters, { FilterOptions } from '../components/AdvancedFilters';
import { apiClient } from '../services/api';
import { useTranslation } from 'react-i18next';
import Seo from '../components/seo/Seo';

const { Option } = Select;
const { Title } = Typography;

const BrandsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { brands, loading, error, pagination } = useAppSelector(
    state => state.brand
  );
  const { user, isAuthenticated } = useAppSelector(state => state.auth);
  const { t } = useTranslation();
  const { message } = App.useApp();

  const [category, setCategory] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [favoriteIdSet, setFavoriteIdSet] = useState<Set<number>>(new Set());
  const [selectedLetter, setSelectedLetter] = useState<string>('');
  const [localSearchQuery, setLocalSearchQuery] = useState<string>('');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterOptions>({
    categories: [],
    minRating: 0,
    maxRating: 5,
    priceRange: [],
    certifications: [],
    countries: [],
    foundedYear: [1900, 2024],
    sortBy: 'overall_score',
    sortOrder: 'desc',
  });

  useEffect(() => {
    loadBrands();
  }, [localSearchQuery, category, currentPage, selectedLetter]);

  // 拉取用户收藏ID集合（一次拉取，前端本地Set判断）
  useEffect(() => {
    const fetchFavoriteIds = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await apiClient.getFavoriteIds();
        const ids = res.data || [];
        setFavoriteIdSet(new Set(ids));
      } catch (error) {
        message.error(t('common.error'));
      }
    };
    fetchFavoriteIds();
  }, [isAuthenticated]);

  const loadBrands = () => {
    const offset = (currentPage - 1) * pagination.limit;

    dispatch(
      fetchBrands({
        search: localSearchQuery || undefined,
        category: category || undefined,
        startsWith: selectedLetter || undefined,
        limit: pagination.limit,
        offset,
      })
    );
  };

  const handleSearch = (value: string) => {
    setLocalSearchQuery(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setCategory(value);
    setCurrentPage(1);
  };

  const handleLetterClick = (letter: string) => {
    setSelectedLetter(letter === selectedLetter ? '' : letter);
    setCurrentPage(1);
    setLocalSearchQuery('');
  };

  const handleViewDetails = (id: number) => {
    navigate(`/brands/${id}`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleApplyFilters = (filters: FilterOptions) => {
    setActiveFilters(filters);
    setCurrentPage(1);
    // 这里可以扩展后端API支持更多筛选参数
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (activeFilters.categories.length > 0) count++;
    if (activeFilters.minRating > 0 || activeFilters.maxRating < 5) count++;
    if (activeFilters.priceRange.length > 0) count++;
    if (activeFilters.certifications.length > 0) count++;
    if (activeFilters.countries.length > 0) count++;
    if (
      activeFilters.foundedYear[0] > 1900 ||
      activeFilters.foundedYear[1] < 2024
    )
      count++;
    return count;
  };

  // 生成A-Z字母导航
  const generateAlphabetNav = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
        }}
      >
        <Button
          size="small"
          type={selectedLetter === '' ? 'primary' : 'default'}
          onClick={() => handleLetterClick('')}
        >
          {t('brands.all')}
        </Button>
        {letters.map(letter => (
          <Button
            key={letter}
            size="small"
            type={selectedLetter === letter ? 'primary' : 'default'}
            onClick={() => handleLetterClick(letter)}
            style={{ minWidth: '32px' }}
          >
            {letter}
          </Button>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Seo
        title={t('brands.title')}
        description={t('brands.search_placeholder')}
        path={'/brands'}
        alternates={[
          { hrefLang: 'en', href: `${(import.meta as any).env.VITE_SITE_URL || ''}/brands?lng=en` },
          { hrefLang: 'zh-CN', href: `${(import.meta as any).env.VITE_SITE_URL || ''}/brands?lng=zh-CN` },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: (brands || []).slice(0, 20).map((b, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            url: `${(import.meta as any).env.VITE_SITE_URL || ''}/brands/${b.id}`,
            name: b.name,
          })),
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2}>{t('brands.title')}</Title>
        {user && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/brands/add')}
          >
            {t('brands.add_brand')}
          </Button>
        )}
      </div>

      {/* A-Z字母导航 */}
      <Affix offsetTop={80}>{generateAlphabetNav()}</Affix>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={16}>
          <SearchSuggestions
            onSearch={handleSearch}
            placeholder={t('brands.search_placeholder')}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={6}>
          <Select
            placeholder={t('brands.select_category')}
            allowClear
            size="large"
            style={{ width: '100%' }}
            value={category || undefined}
            onChange={handleCategoryChange}
          >
            <Option value="fashion">{t('brands.fashion')}</Option>
            <Option value="accessories">{t('brands.accessories')}</Option>
            <Option value="leather goods">{t('brands.leather')}</Option>
            <Option value="jewelry">{t('brands.jewelry')}</Option>
          </Select>
        </Col>
        <Col span={2}>
          <Button
            size="large"
            icon={<FilterOutlined />}
            onClick={() => setFiltersVisible(true)}
            style={{ width: '100%' }}
            type={getActiveFiltersCount() > 0 ? 'primary' : 'default'}
          >
            {t('brands.filter')}{' '}
            {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
          </Button>
        </Col>
      </Row>

      <AdvancedFilters
        visible={filtersVisible}
        onClose={() => setFiltersVisible(false)}
        onApplyFilters={handleApplyFilters}
        initialFilters={activeFilters}
      />

      {error && (
        <Alert
          message={t('brands.loading_failed')}
          description={error}
          type="error"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading}>
        {brands.length > 0 ? (
          <>
            <Row gutter={[16, 16]}>
              {brands.map(brand => (
                <Col span={8} key={brand.id}>
                  <BrandCard
                    brand={brand}
                    onViewDetails={handleViewDetails}
                    initialFavoriteStatus={favoriteIdSet.has(brand.id)}
                  />
                </Col>
              ))}
            </Row>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Pagination
                current={currentPage}
                total={pagination.total}
                pageSize={pagination.limit}
                onChange={handlePageChange}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) =>
                  t('brands.pagination_total', {
                    start: range[0],
                    end: range[1],
                    total,
                  })
                }
              />
            </div>
          </>
        ) : (
          !loading && (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <p>{t('brands.no_data')}</p>
            </div>
          )
        )}
      </Spin>
    </div>
  );
};

export default BrandsPage;
